import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import styles from './styles.module.scss';
import { RowMenuAction } from '@components-clean/core/grid/grid.interface';
import { Popup } from '@components-clean/core/popup/popup';
import Tooltip from '@components-clean/core/tooltip';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import { useOnClickOutside } from '@hooks/index';

type MenuActionProps = {
    actions: RowMenuAction[];
    dataItem: unknown;
    excludeHiddenRowMenuActions?: boolean;
};

export const MenuActions = ({ actions, dataItem, excludeHiddenRowMenuActions }: MenuActionProps) => {
    const [popupOpen, setPopupOpen] = useState<boolean>(false);
    const offSetRef = useRef(0);
    const anchorRef = React.useRef<HTMLButtonElement | null>(null);
    const { readOnly } = useReadOnlyContext();
    const menuItemRef = useRef<HTMLDivElement | null>(null);

    const toggleMenu = () => {
        if (anchorRef.current) {
            const bodyRect = document.body.getBoundingClientRect();
            const elemRect = anchorRef.current.getBoundingClientRect();
            offSetRef.current = bodyRect.bottom - elemRect.top;
            setPopupOpen(!popupOpen);
        }
    };

    useOnClickOutside(
        menuItemRef as React.RefObject<HTMLDivElement>,
        () => {
            toggleMenu();
        },
        [anchorRef as React.RefObject<HTMLElement>],
    );

    // Filter actions here use unfiltered count to display total amount of columns
    // Which sets the width of the column
    const filteredActionItems = actions.filter((x) => {
        return x.showMenuItem ? x.showMenuItem(dataItem) : true;
    });

    if (excludeHiddenRowMenuActions ? filteredActionItems.length <= 2 : actions.length <= 2)
        return (
            <Tooltip position="top" parentTitle>
                <div className="k-d-flex k-d-flex-row k-gap-1 k-mx-1 k-justify-content-center">
                    {filteredActionItems.map((a) => (
                        <button
                            aria-label={a.label}
                            key={a.id}
                            disabled={a.disabled || readOnly || a.rowDisabled?.(dataItem)}
                            title={a.label}
                            type="button"
                            onClick={() => a.onClick(dataItem)}
                            className={cn('k-bg-transparent k-border-0 k-p-0', styles.button, {
                                'k-cursor-pointer': !a.disabled && !readOnly && !a.rowDisabled?.(dataItem),
                            })}
                        >
                            <FontAwesomeIcon
                                icon={a.icon}
                                fontSize={16}
                                className={cn(a.colorProp === 'Red' ? styles.redIcon : styles.icon, styles.iconSingle, {
                                    [styles.disabled]: a.disabled || readOnly || a.rowDisabled?.(dataItem),
                                })}
                            />
                        </button>
                    ))}
                </div>
            </Tooltip>
        );

    return (
        <>
            <button
                ref={anchorRef}
                aria-label="actions"
                type="button"
                onClick={() => toggleMenu()}
                className={cn('k-bg-transparent k-border-0 k-p-0 k-cursor-pointer', styles.button, styles.menu)}
            >
                <FontAwesomeIcon
                    icon={['fas', 'ellipsis-vertical']}
                    fontSize={14}
                    className={cn({ [styles.ellipsisOpen]: popupOpen })}
                />
            </button>
            <Popup
                data-testid="row-menu-popover"
                anchor={anchorRef.current}
                show={popupOpen}
                anchorAlign={{ horizontal: 'right', vertical: offSetRef.current < 100 ? 'bottom' : 'top' }}
                popupAlign={{ horizontal: 'left', vertical: 'top' }}
                onBlur={() => setPopupOpen(false)}
                className={styles.popup}
                border="noBorder"
                closeOnBlur={false}
            >
                <div ref={menuItemRef}>
                    {filteredActionItems.map((a) => (
                        <button
                            aria-label={a.label}
                            key={a.id}
                            disabled={a.disabled || readOnly || a.rowDisabled?.(dataItem)}
                            type="button"
                            onClick={() => {
                                setPopupOpen(false);
                                a.onClick(dataItem);
                            }}
                            className={cn(
                                'k-bg-transparent k-border-0 k-p-0 k-d-flex k-align-items-center k-w-full',
                                styles.action,
                                styles.button,
                                {
                                    'k-cursor-pointer': !a.disabled && !readOnly && !a.rowDisabled?.(dataItem),
                                },
                            )}
                        >
                            <div className="k-w-5.5 k-mr-2 k-flex-shrink-0">
                                <FontAwesomeIcon
                                    icon={a.icon}
                                    fontSize={16}
                                    className={cn(a.colorProp === 'Red' ? styles.redIcon : styles.icon, {
                                        [styles.disabled]: a.disabled || readOnly || a.rowDisabled?.(dataItem),
                                    })}
                                />
                            </div>
                            <span
                                className={cn('k-text-left', {
                                    [styles.disabled]: a.disabled || readOnly || a.rowDisabled?.(dataItem),
                                })}
                            >
                                {a.label}
                            </span>
                        </button>
                    ))}
                </div>
            </Popup>
        </>
    );
};
